import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Input,
  Space,
  Divider,
  Badge,
  Spin,
} from 'antd';
import {
  EyeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SettingOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { fetchUserPermissionList } from '../../slices/user';

const { Title, Text } = Typography;

const PermissionsModal = ({ visible, onClose, userData, userId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionsData, setPermissionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (visible && userId) {
      setLoading(true);
      dispatch(fetchUserPermissionList({ user_id: userId }))
        .then(res => {
          if (res?.payload?.success && res.payload.data) {
            const formattedData = formatPermissionsData(
              res.payload.data.permission,
            );
            setPermissionData(formattedData);
          } else {
            console.error('Failed to fetch:', res);
            setPermissionData([]);
          }
        })
        .catch(err => {
          console.error('Error:', err);
          setPermissionData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, userId, visible]);
  const formatPermissionsData = permissions => {
    const categories = {};

    permissions.forEach(permission => {
      const match = permission.match(/Can (\w+) (.+)/);
      if (match) {
        const action = match[1];
        const model = match[2];
        const { category, icon, color } = getCategoryInfo(model);

        if (!categories[category]) {
          categories[category] = {
            category,
            icon,
            color,
            permissions: [],
          };
        }

        categories[category].permissions.push({
          action,
          label: permission,
          granted: true, // Assuming all fetched permissions are granted
        });
      }
    });

    return Object.values(categories);
  };
  const getCategoryInfo = model => {
    const modelLower = model.toLowerCase();

    if (modelLower.includes('user') || modelLower.includes('profile')) {
      return {
        category: 'User Management',
        icon: <UserOutlined />,
        color: '#1890ff',
      };
    } else if (
      modelLower.includes('customer') ||
      modelLower.includes('client')
    ) {
      return {
        category: 'Customer Management',
        icon: <TeamOutlined />,
        color: '#52c41a',
      };
    } else if (
      modelLower.includes('location') ||
      modelLower.includes('site') ||
      modelLower.includes('address')
    ) {
      return {
        category: 'Location Management',
        icon: <EnvironmentOutlined />,
        color: '#fa8c16',
      };
    } else if (
      modelLower.includes('group') ||
      modelLower.includes('permission') ||
      modelLower.includes('auth')
    ) {
      return {
        category: 'Access Control',
        icon: <SettingOutlined />,
        color: '#722ed1',
      };
    } else if (modelLower.includes('report') || modelLower.includes('log')) {
      return {
        category: 'Reports & Logs',
        icon: <FileTextOutlined />,
        color: '#13c2c2',
      };
    } else {
      return {
        category: 'General',
        icon: <ShopOutlined />,
        color: '#eb2f96',
      };
    }
  };

  const getActionIcon = action => {
    switch (action.toLowerCase()) {
      case 'view':
        return <EyeOutlined />;
      case 'add':
        return <PlusOutlined />;
      case 'change':
      case 'edit':
      case 'update':
        return <EditOutlined />;
      case 'delete':
        return <DeleteOutlined />;
      default:
        return <SettingOutlined />;
    }
  };

  const getActionColor = (action, granted) => {
    if (!granted) return '#d9d9d9';

    switch (action.toLowerCase()) {
      case 'view':
        return '#1890ff';
      case 'add':
        return '#52c41a';
      case 'change':
      case 'edit':
      case 'update':
        return '#fa8c16';
      case 'delete':
        return '#ff4d4f';
      default:
        return '#722ed1';
    }
  };

  const filteredPermissions = permissionsData.filter(
    category =>
      category.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.permissions.some(perm =>
        perm.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  const totalPermissions = permissionsData.reduce(
    (total, category) => total + category.permissions.length,
    0,
  );

  const grantedPermissions = permissionsData.reduce(
    (total, category) =>
      total + category.permissions.filter(perm => perm.granted).length,
    0,
  );

  return (
    <Modal
      title={
        <div
          style={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            margin: '-24px -24px 24px -24px',
            padding: '20px',
            color: 'white',
          }}
        >
          <UserOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            User Permissions
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
            {userData?.name || 'User'} • {userData?.email || 'N/A'}
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '6px',
            fontWeight: '500',
          }}
        >
          Close
        </Button>,
      ]}
      styles={{
        body: { padding: '24px', maxHeight: '70vh', overflowY: 'auto' },
      }}
    >
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              tip="Loading permissions..."
            />
          </div>
        ) : (
          <>
            <Input
              placeholder="Search permissions..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                marginBottom: '24px',
                borderRadius: '8px',
                height: '40px',
                fontSize: '14px',
              }}
            />
            <Row gutter={[16, 16]}>
              {filteredPermissions.map((category, index) => (
                <Col span={12} key={index}>
                  <Card
                    style={{
                      borderRadius: '12px',
                      border: `2px solid ${category.color}20`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      height: '100%',
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <Space align="center">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${category.color}20, ${category.color}40)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            color: category.color,
                          }}
                        >
                          {category.icon}
                        </div>
                        <div>
                          <Title
                            level={5}
                            style={{ margin: 0, color: '#262626' }}
                          >
                            {category.category}
                          </Title>
                          <Badge
                            count={
                              category.permissions.filter(p => p.granted).length
                            }
                            style={{
                              backgroundColor: category.color,
                              fontSize: '11px',
                            }}
                          />
                        </div>
                      </Space>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    <div>
                      {category.permissions.map((permission, permIndex) => (
                        <div
                          key={permIndex}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            marginBottom: '8px',
                            borderRadius: '8px',
                            background: permission.granted
                              ? `${getActionColor(permission.action, true)}10`
                              : '#fafafa',
                            border: `1px solid ${permission.granted
                                ? getActionColor(permission.action, true) + '30'
                                : '#f0f0f0'
                              }`,
                          }}
                        >
                          <Space>
                            <div
                              style={{
                                fontSize: '14px',
                                color: getActionColor(
                                  permission.action,
                                  permission.granted,
                                ),
                              }}
                            >
                              {getActionIcon(permission.action)}
                            </div>
                            <Text
                              style={{
                                fontSize: '13px',
                                color: permission.granted
                                  ? '#262626'
                                  : '#8c8c8c',
                                fontWeight: permission.granted ? '500' : '400',
                              }}
                            >
                              {permission.label}
                            </Text>
                          </Space>
                          <Tag
                            color={permission.granted ? 'success' : 'default'}
                            style={{
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500',
                              border: 'none',
                            }}
                          >
                            {permission.granted ? 'Granted' : 'Denied'}
                          </Tag>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {filteredPermissions.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">
                  {permissionsData.length === 0
                    ? 'No permissions found for this user.'
                    : 'No permissions found matching your search.'}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PermissionsModal;
